import React, { FC, useContext } from "react";
import { useTranslation } from "react-i18next";

import { MenuItem, NestedMenu } from "@/components/Navigation";
import { UserContext } from "@/components/User";
import { isProduction } from "@/core/environments";
import { Flex } from "@/eworkitComponents";
import { EworkPlus } from "@/views/components/Layout/components/Navigation/EworkPlus";
import { INavigationProps } from "@/views/components/Layout/components/Navigation/Navigation";
import { RouteNavItem } from "@/views/components/Layout/components/Topbar/components/RouteNavItem";
import { router } from "@/views/router";

import { HelpCenter } from "./HelpCenter";
import { TimeReports } from "./TimeReport";

interface INavigationDesktopProps extends INavigationProps {}

export const NavigationMediumScreen: FC<INavigationDesktopProps> = () => {
  const { t } = useTranslation("session");
  const User = useContext(UserContext);
  const isManager = User.hasCompanyManagerPrivileges();

  const baseClass = "navigation";

  const dashboardRoute = router.get("dashboard");
  const jobRequestsRoute = router.get("job-requests");
  const jobApplicationsRoute = router.get("job-applications");
  const assignmentsRoute = router.get("assignments");
  const myTeamRoute = router.get("companies.team");
  const companySettingsRoute = router.get("company.settings");
  const timeReportsRoute = router.get("time-reports");
  const contractsRoute = router.get("contracts");

  return (
    <Flex className={baseClass} direction="row" align="center" justify="flex-start" innerMargin="md">
      <MenuItem
        as={RouteNavItem}
        path={dashboardRoute.path}
        exact={false}
        variant="text"
        text={t("COMPONENTS.TOPBAR.BUTTON_DASHBOARD")}
        testName={dashboardRoute.testName}
      />
      <MenuItem
        as={NestedMenu}
        level={1}
        key="view"
        buttonContent={t("COMPONENTS.TOPBAR.BUTTON_MORE")}
        className={`${baseClass}--nested`}>
        <div>
          <MenuItem
            as={RouteNavItem}
            path={jobRequestsRoute.path}
            exact={false}
            variant="text"
            text={t("COMPONENTS.TOPBAR.BUTTON_JOB_REQUESTS")}
          />
        </div>
        <div className={`${baseClass}--nested--menu`}>
          <MenuItem
            as={NestedMenu}
            level={2}
            key="view"
            buttonContent={t("COMPONENTS.TOPBAR.BUTTON_VIEW")}
            className={`${baseClass}--nested--3rd`}>
            {User.hasRouteAccess(assignmentsRoute) && (
              <RouteNavItem
                path={assignmentsRoute.path}
                exact={false}
                variant="nested"
                icon="type_assignment"
                text={t("COMPONENTS.TOPBAR.BUTTON_ASSIGNMENTS")}
              />
            )}
            <RouteNavItem
              path={jobApplicationsRoute.path}
              exact={false}
              variant="nested"
              icon="type_application"
              text={t("COMPONENTS.TOPBAR.BUTTON_APPLICATIONS")}
            />
            {!isProduction && User.contextCompany && (
              <RouteNavItem
                path={timeReportsRoute.path}
                exact={false}
                variant="nested"
                icon="type_time_report"
                text={t("COMPONENTS.TOPBAR.BUTTON_TIME_REPORTS")}
              />
            )}
            {User.hasRouteAccess(contractsRoute) && (
              <RouteNavItem
                path={contractsRoute.path}
                exact={false}
                variant="nested"
                icon="type_application"
                text={t("COMPONENTS.TOPBAR.BUTTON_CONTRACTS")}
              />
            )}
          </MenuItem>

          {isManager && (
            <MenuItem
              as={NestedMenu}
              level={2}
              key="company"
              buttonContent={t("COMPONENTS.TOPBAR.BUTTON_MY_COMPANY")}
              className={`${baseClass}--nested--3rd`}>
              <RouteNavItem
                path={myTeamRoute.path}
                exact={false}
                variant="nested"
                icon="users"
                text={t("COMPONENTS.TOPBAR.BUTTON_MY_TEAM")}
                testName={myTeamRoute.testName}
              />
              <RouteNavItem
                path={companySettingsRoute.path}
                exact={false}
                variant="nested"
                icon="settings"
                text={t("COMPONENTS.TOPBAR.BUTTON_SETTINGS")}
              />
            </MenuItem>
          )}

          <EworkPlus variant="medium" />
          <TimeReports />
          <HelpCenter />
        </div>
      </MenuItem>
    </Flex>
  );
};
